import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Gift, Package, MapPin, Truck, CheckCircle, Clock, XCircle, Loader2, ChevronDown, ChevronUp, Phone, AlertTriangle } from 'lucide-react'
import { exchangesApi, hospitalsApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'

const PAGE_SIZE = 10

const UserExchangeRecords = () => {
  usePageTitle()
  const navigate = useNavigate()
  const [records, setRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [stats, setStats] = useState({ total: 0, completed: 0, active: 0, cancelled: 0 })
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  // 医院详情缓存：hospital_id -> { address, phone, remark }
  const [hospitalsMap, setHospitalsMap] = useState<Record<string, any>>({})
  // 展开详情的记录 id 集合
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  // 取消确认弹窗
  const [cancelModal, setCancelModal] = useState<{ open: boolean; record: any }>({ open: false, record: null })
  const [cancelling, setCancelling] = useState(false)

  // 登录守卫
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/user/login')
    }
  }, [navigate])
  // 确认收货弹窗
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; record: any }>({ open: false, record: null })
  const [confirming, setConfirming] = useState(false)

  const totalPages = Math.ceil(total / PAGE_SIZE)

  // 加载统计数据
  useEffect(() => {
    const statsParams: any = {}
    const stored = localStorage.getItem('userAccount')
    if (stored) {
      try {
        const currentUser = JSON.parse(stored)
        if (currentUser?.id) statsParams.user_id = currentUser.id
      } catch { /* ignore */ }
    }
    exchangesApi.stats(statsParams).then((res: any) => {
      if (res) {
        setStats({
          total: res.total || 0,
          completed: res.completed || 0,
          active: res.active || 0,
          cancelled: res.cancelled || 0,
        })
      }
    }).catch(console.error)
  }, [])

  // 加载分页列表
  useEffect(() => {
    window.scrollTo(0, 0)
    setLoading(true)
    const params: any = { page, page_size: PAGE_SIZE }
    if (statusFilter !== 'all') params.status = statusFilter

    // 传入当前用户 ID，确保只展示当前用户的兑换记录
    const stored = localStorage.getItem('userAccount')
    if (stored) {
      try {
        const currentUser = JSON.parse(stored)
        if (currentUser?.id) params.user_id = currentUser.id
      } catch { /* ignore */ }
    }

    exchangesApi.list(params).then(async (res: any) => {
      const items = res?.exchanges || []

      // 收集自提订单的唯一 hospital_id，批量请求医院详情
      const selfpickupHospitalIds = [
        ...new Set(
          items
            .filter((r: any) => r.delivery_type === 'selfpickup' && r.hospital_id)
            .map((r: any) => r.hospital_id as string)
        ),
      ]

      if (selfpickupHospitalIds.length > 0) {
        const results = await Promise.allSettled(
          selfpickupHospitalIds.map((id) => hospitalsApi.detail(id))
        )
        const map: Record<string, any> = {}
        results.forEach((res, idx) => {
          if (res.status === 'fulfilled' && res.value) {
            map[selfpickupHospitalIds[idx]] = res.value
          }
        })
        setHospitalsMap(map)
      }

      const mapped = items.map((r: any) => ({
        id: r.id,
        orderNo: r.order_no || r.id,
        giftId: r.gift_id,
        giftName: r.gift_name || '未知礼品',
        giftImage: r.gift_image || r.gift?.image || r.image || '',
        points: r.points,
        status: r.status,
        createdAt: r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '',
        deliveryType: r.delivery_type === 'express' ? 'express' : 'selfpickup',
        hospitalId: r.hospital_id || '',
        hospital: r.hospital_name || '',
        address: r.address || '',
        receiverName: r.receiver_name || '',
        phone: r.phone || '',
        confirmedAt: r.confirmed_at ? new Date(r.confirmed_at).toLocaleString('zh-CN') : '',
        trackingNo: r.tracking_no || '',
      }))
      // 默认展开第一个自提订单的机构信息
      const firstSelfpickup = mapped.find(
        (r: any) => r.deliveryType === 'selfpickup' && (r.status === 'pending' || r.status === 'completed')
      )
      if (firstSelfpickup) {
        setExpandedIds(new Set([firstSelfpickup.id]))
      }

      setRecords(mapped)
      setTotal(res?.total || 0)
    }).catch(console.error).finally(() => setLoading(false))
  }, [page, statusFilter])

  const handleStatusChange = (status: string) => {
    setStatusFilter(status)
    setPage(1)
  }

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // 取消订单
  const handleCancel = async () => {
    if (!cancelModal.record) return
    setCancelling(true)
    try {
      await exchangesApi.cancel(cancelModal.record.id)
      setCancelModal({ open: false, record: null })
      // 重新加载数据
      setPage(1)
      setStatusFilter('all')
    } catch (err: any) {
      alert(err?.message || err?.detail || '取消失败')
    } finally {
      setCancelling(false)
    }
  }

  // 确认收货
  const handleConfirm = async () => {
    if (!confirmModal.record) return
    setConfirming(true)
    try {
      await exchangesApi.userConfirm(confirmModal.record.id)
      setConfirmModal({ open: false, record: null })
      setPage(1)
      setStatusFilter('all')
    } catch (err: any) {
      alert(err?.message || err?.detail || '确认失败')
    } finally {
      setConfirming(false)
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed': return { label: '已完成', icon: CheckCircle, bg: 'bg-green-100', textColor: 'text-green-700' }
      case 'pending': return { label: '待领取', icon: Clock, bg: 'bg-orange-100', textColor: 'text-orange-700' }
      case 'confirmed': return { label: '待发货', icon: Clock, bg: 'bg-blue-100', textColor: 'text-blue-700' }
      case 'shipping': return { label: '配送中', icon: Truck, bg: 'bg-blue-100', textColor: 'text-blue-700' }
      case 'delivered': return { label: '已发货', icon: Package, bg: 'bg-purple-100', textColor: 'text-purple-700' }
      case 'cancelled': return { label: '已取消', icon: XCircle, bg: 'bg-gray-100', textColor: 'text-gray-700' }
      default: return { label: status, icon: Clock, bg: 'bg-gray-100', textColor: 'text-gray-700' }
    }
  }

  // 自提医院详情展示块（pending / completed 通用）
  const renderSelfpickupBlock = (record: any, colorScheme: 'orange' | 'green') => {
    const hospital = hospitalsMap[record.hospitalId]
    const expanded = expandedIds.has(record.id)
    const hasDetail = hospital && (hospital.address || hospital.phone || hospital.remark)

    const colors = colorScheme === 'orange'
      ? { bg: 'bg-orange-50', title: 'text-orange-800', body: 'text-orange-600', sub: 'text-orange-500', border: 'border-orange-200', btn: 'text-orange-500 hover:text-orange-700', icon: 'text-orange-600' }
      : { bg: 'bg-green-50', title: 'text-green-800', body: 'text-green-600', sub: 'text-green-500', border: 'border-green-200', btn: 'text-green-500 hover:text-green-700', icon: 'text-green-600' }

    const titleLabel = colorScheme === 'orange' ? '医院自提' : '已领取 · 自提'

    return (
      <div className={`mt-3 p-3 ${colors.bg} rounded-lg`}>
        <div className="flex items-start space-x-2">
          <MapPin className={`w-4 h-4 ${colors.icon} mt-0.5 flex-shrink-0`} />
          <div className="flex-1 text-sm">
            {/* 标题行 + 查看详情按钮 */}
            <div className="flex items-center justify-between">
              <p className={`${colors.title} font-medium`}>{titleLabel}</p>
              {hasDetail && (
                <button
                  onClick={() => toggleExpand(record.id)}
                  className={`flex items-center gap-0.5 text-xs ${colors.btn} transition-colors`}
                >
                  {expanded ? '收起' : '查看详情'}
                  {expanded
                    ? <ChevronUp className="w-3 h-3" />
                    : <ChevronDown className="w-3 h-3" />
                  }
                </button>
              )}
            </div>

            {/* 医院名称 */}
            <p className={`${colors.body} mt-1`}>{record.hospital}</p>

            {/* 展开后显示详细信息 */}
            {expanded && hasDetail && (
              <div className={`mt-2 pt-2 border-t ${colors.border} space-y-1`}>
                {hospital.address && (
                  <p className={`${colors.body}`}>
                    <span className="font-medium">地址：</span>{hospital.address}
                  </p>
                )}
                {hospital.phone && (
                  <p className={`${colors.body} flex items-center gap-1`}>
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    <span className="font-medium">电话：</span>
                    <a href={`tel:${hospital.phone}`} className="underline underline-offset-2">
                      {hospital.phone}
                    </a>
                  </p>
                )}
                {hospital.remark && (
                  <p className={`${colors.sub}`}>
                    <span className="font-medium">说明：</span>{hospital.remark}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <h1 className="text-xl font-bold text-white">兑换记录</h1>
        <p className="text-green-100 text-sm mt-1">查看积分礼品兑换历史</p>
        <div className="grid grid-cols-4 gap-2 mt-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 text-center">
            <p className="text-white font-bold text-lg">{stats.total}</p>
            <p className="text-green-100 text-xs">全部</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 text-center">
            <p className="text-white font-bold text-lg">{stats.completed}</p>
            <p className="text-green-100 text-xs">已完成</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 text-center">
            <p className="text-white font-bold text-lg">{stats.active}</p>
            <p className="text-green-100 text-xs">进行中</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 text-center">
            <p className="text-white font-bold text-lg">{stats.cancelled}</p>
            <p className="text-green-100 text-xs">已取消</p>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-md p-2 flex space-x-2 overflow-x-auto">
          {[
            { key: 'all', label: '全部' },
            { key: 'pending', label: '待领取' },
            { key: 'confirmed', label: '待发货' },
            { key: 'shipping', label: '配送中' },
            { key: 'completed', label: '已完成' },
            { key: 'cancelled', label: '已取消' },
          ].map((filter) => (
            <button key={filter.key} onClick={() => handleStatusChange(filter.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === filter.key ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 mt-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">暂无兑换记录</p>
            <p className="text-sm text-gray-400 mt-1">快去兑换喜欢的礼品吧~</p>
            <Link to="/user/exchange" className="inline-block mt-4 px-6 py-2 bg-green-700 text-white rounded-xl text-sm font-medium">
              去兑换
            </Link>
          </div>
        ) : (
          records.map((record) => {
            const statusConfig = getStatusConfig(record.status)
            return (
              <div key={record.id} className="bg-white rounded-xl shadow-md p-4">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {record.giftImage ? (
                      <img src={record.giftImage} alt={record.giftName} className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none' }} />
                    ) : (
                      <Gift className="w-8 h-8 text-orange-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800">{record.giftName}</h3>
                        <p className="text-xs text-gray-500 mt-1 truncate">单号：{record.orderNo.toUpperCase()}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {(record.status === 'pending' || record.status === 'confirmed') && (
                          <button
                            onClick={() => setCancelModal({ open: true, record })}
                            className="px-2 py-1 text-xs text-red-600 bg-red-50 rounded-full hover:bg-red-100"
                          >
                            取消
                          </button>
                        )}
                        {record.status === 'shipping' && (
                          <button
                            onClick={() => setConfirmModal({ open: true, record })}
                            className="px-2 py-1 text-xs text-green-700 bg-green-50 rounded-full hover:bg-green-100"
                          >
                            确认收货
                          </button>
                        )}
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.textColor}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                      <div>
                        <p className="text-gray-500">兑换时间</p>
                        <p className="text-gray-800">{record.createdAt}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">消耗积分</p>
                        <p className="text-orange-500 font-bold">-{record.points}</p>
                      </div>
                      {record.confirmedAt && !(record.status === 'completed' && record.deliveryType === 'express') && (
                        <div>
                          <p className="text-gray-500">领取时间</p>
                          <p className="text-gray-800">{record.confirmedAt}</p>
                        </div>
                      )}
                    </div>

                    {/* 待领取 - 自提 */}
                    {record.status === 'pending' && record.deliveryType === 'selfpickup' &&
                      renderSelfpickupBlock(record, 'orange')}

                    {/* 已完成 - 自提 */}
                    {record.status === 'completed' && record.deliveryType === 'selfpickup' &&
                      renderSelfpickupBlock(record, 'green')}

                    {/* 已完成 - 快递，显示快递单号 */}
                    {record.status === 'completed' && record.deliveryType === 'express' && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Package className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-sm">
                            <p className="text-green-800 font-medium">已完成 · 快递配送</p>
                            {record.trackingNo && (
                              <p className="text-green-600 mt-0.5">快递单号：{record.trackingNo}</p>
                            )}
                            <p className="text-green-600 mt-0.5">收件人：{record.receiverName} | {record.phone}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 待领取 - 快递 */}
                    {record.status === 'pending' && record.deliveryType === 'express' && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Truck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-sm">
                            <p className="text-blue-800 font-medium">快递配送</p>
                            <p className="text-blue-600 mt-0.5">收件人：{record.receiverName} | {record.phone}</p>
                            <p className="text-blue-500 text-xs mt-1">地址：{record.address}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 待发货 */}
                    {record.status === 'confirmed' && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Truck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-sm">
                            <p className="text-blue-800 font-medium">待发货</p>
                            {record.deliveryType === 'express' && (
                              <>
                                <p className="text-blue-600 mt-0.5">收件人：{record.receiverName} | {record.phone}</p>
                                <p className="text-blue-500 text-xs mt-1">地址：{record.address}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 配送中 */}
                    {record.status === 'shipping' && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Truck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="text-blue-800 font-medium">配送中</p>
                            <p className="text-blue-600 mt-0.5">收件人：{record.receiverName} | {record.phone}</p>
                            {record.trackingNo && <p className="text-blue-500 text-xs mt-1">快递单号：{record.trackingNo}</p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 已发货 */}
                    {record.status === 'delivered' && (
                      <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Package className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="text-purple-800 font-medium">已发货</p>
                            <p className="text-purple-600 mt-0.5">收件人：{record.receiverName} | {record.phone}</p>
                            {record.trackingNo && <p className="text-purple-500 text-xs mt-1">快递单号：{record.trackingNo}</p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 已取消 */}
                    {record.status === 'cancelled' && (
                      <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <XCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="text-gray-600 font-medium">已取消</p>
                            <p className="text-gray-500 mt-0.5">该兑换记录已取消，积分已返还</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
})
        )}

        {/* 取消订单确认弹窗 */}
        {cancelModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">确认取消订单</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                确定要取消兑换 <span className="font-bold text-gray-800">{cancelModal.record?.giftName}</span> 吗？取消后积分将原路退回。
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setCancelModal({ open: false, record: null })}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium"
                >
                  暂不取消
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-medium disabled:opacity-50"
                >
                  {cancelling ? '取消中...' : '确认取消'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 确认收货弹窗 */}
        {confirmModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">确认收货</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                确定已收到礼品 <span className="font-bold text-gray-800">{confirmModal.record?.giftName}</span> 吗？
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setConfirmModal({ open: false, record: null })}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium"
                >
                  暂不确认
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-medium disabled:opacity-50"
                >
                  {confirming ? '确认中...' : '确认收货'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 分页 */}
        {!loading && records.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4 pb-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              上一页
            </button>
            <span className="text-sm text-gray-500">
              第 {page} / {totalPages} 页，共 {total} 条
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserExchangeRecords
